import select from 'dom-select';
import { breakpointCallback, cl, CustomElement } from '@/utils';
import debounce from 'lodash.debounce';

enum Comp {
  OPEN = 'accordion-open',
  OPEN_ATTR = 'open',
}

export default class Accordion extends CustomElement {
  static component = 'b-accordion';
  static observedAttributes = ['open', 'duration', 'name'];
  busy = false;
  isOpen = this.getAttribute(Comp.OPEN_ATTR) === 'true';
  duration = Number(this.getAttribute('duration')) || 700;
  name = this.getAttribute('name') || 'accordion';
  keep = this.getAttribute('keep-other') !== null || false;
  breakpoint = this.getAttribute('breakpoint');
  autoHeight = this.getAttribute('auto-height') !== null || true;

  trigger: HTMLElement | null = null;
  content: HTMLElement | null = null;
  inner: HTMLElement | null = null;

  init() {
    if (this.isOpen) {
      this.open();
    }

    this.trigger = this.querySelector(':scope .accordion__trigger');
    this.content = this.querySelector(':scope .accordion__content');
    this.inner = this.content.querySelector('.accordion__inner');

    if (this.name !== 'accordion') {
      this.trigger = select(`[data-accordion="${this.name}"]`);
    }

    if (this.trigger) {
      if (this.breakpoint) {
        this.classList.add(`${this.breakpoint}-accordion`);
        breakpointCallback(this.breakpoint as any, ({ matches }) => {
          if (matches) {
            this.trigger.addEventListener('click', this.toggle);
          } else {
            this.trigger.removeEventListener('click', this.toggle);
          }
        });
      } else {
        this.trigger.addEventListener('click', this.toggle);
      }
    }

    if (this.autoHeight) {
      this.resize();
      window.addEventListener('resize', this.resize);
    }
  }

  resize = debounce(() => {
    this.style.setProperty('--accordion-height', this.getHeight());
  }, 500);

  changed(name: string, newValue: string) {
    if (name === Comp.OPEN_ATTR) {
      if (newValue === 'true') {
        this.open();
      } else {
        this.close(true);
      }
    }
  }

  open() {
    if (this.busy) {
      return;
    }

    this.busy = true;

    if (!this.keep) {
      this.closeAll();
    }

    cl(this.parentElement).add('has-open-accordion');
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.isOpen = true;

        this.busy = false;
      }, this.duration);
    });
  }

  closeAll() {
    select.all<Accordion>(Accordion.component).forEach((acc) => {
      if (acc !== this) {
        acc.close();
      }
    });
  }

  close(skipRemove = false) {
    this.busy = true;
    if (!skipRemove) {
      this.removeAttribute(Comp.OPEN_ATTR);
    }
    cl(this.parentElement).remove('has-open-accordion');
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.isOpen = false;
        this.busy = false;
      }, this.duration);
    });
  }

  openByName(name: string) {
    if (this.name === name) this.open();
  }

  toggle = () => {
    if (this.busy) {
      return;
    }

    if (this.hasAttribute(Comp.OPEN_ATTR)) {
      this.removeAttribute(Comp.OPEN_ATTR);
    } else {
      this.setAttribute(Comp.OPEN_ATTR, 'true');
    }
  };

  getHeight() {
    return `${this.inner?.getBoundingClientRect().height}px` || '0px';
  }

  destroyed() {
    window.removeEventListener('resize', this.resize);
  }
}
